/**
 * update-timing — tiny interaction/visibility timing tracker for AutoUpdater.
 *
 * Answers two scheduling questions for the silent auto-apply:
 *
 *   - isArrivalMoment(windowMs): did the user JUST load the page or JUST
 *     return to the tab? (The version probe fires at exactly those moments,
 *     so an update detected then can be applied immediately — the reload is
 *     invisible to a user who has only just arrived.)
 *   - isIdle(thresholdMs): is the document hidden, or has the user not
 *     interacted (keydown / pointerdown / touchstart) for at least the
 *     threshold? (The safe-moment gate for mid-session detections — protects
 *     non-CRDT surfaces like a sign-up form mid-fill from losing input.)
 *
 * SSR-safe: module state uses Date.now() only; listeners attach inside
 * initUpdateTiming() behind window/document guards (same pattern as
 * version-probe.ts). All listeners are passive — this module never blocks
 * scrolling or input.
 */

// ─── Module state ─────────────────────────────────────────────────────────────

/** When this module initialised — proxy for "the page just loaded". */
let pageLoadedAt = Date.now();

/** Last keydown / pointerdown / touchstart. Starts at page load. */
let lastInteractionAt = pageLoadedAt;

/** Last visibilitychange → visible. Starts at page load. */
let lastBecameVisibleAt = pageLoadedAt;

let initialized = false;

function onInteraction(): void {
  lastInteractionAt = Date.now();
}

function onVisibilityChange(): void {
  if (!document.hidden) {
    lastBecameVisibleAt = Date.now();
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Register the interaction + visibility listeners.
 *
 * Idempotent — calling more than once is a no-op after the first call.
 * No-ops entirely when window/document are unavailable (SSR-safe).
 */
export function initUpdateTiming(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (initialized) return;
  initialized = true;

  window.addEventListener("keydown", onInteraction, { passive: true });
  window.addEventListener("pointerdown", onInteraction, { passive: true });
  window.addEventListener("touchstart", onInteraction, { passive: true });
  document.addEventListener("visibilitychange", onVisibilityChange);
}

/**
 * True when the document is hidden, OR the user has not interacted for at
 * least `thresholdMs`. Either condition makes a silent reload safe.
 */
export function isIdle(thresholdMs: number): boolean {
  if (typeof document !== "undefined" && document.hidden) return true;
  return Date.now() - lastInteractionAt >= thresholdMs;
}

/**
 * True when the page loaded, or the tab became visible, less than `windowMs`
 * ago — the user has JUST arrived, so a reload is invisible.
 */
export function isArrivalMoment(windowMs: number): boolean {
  const now = Date.now();
  return now - pageLoadedAt < windowMs || now - lastBecameVisibleAt < windowMs;
}

/**
 * Detach listeners and reset all timestamps to "now".
 *
 * @internal — test-only export. Lets tests re-anchor pageLoadedAt under fake
 *   timers without vi.resetModules(). Never call from application code.
 */
export function resetUpdateTimingForTesting(): void {
  if (initialized && typeof window !== "undefined" && typeof document !== "undefined") {
    window.removeEventListener("keydown", onInteraction);
    window.removeEventListener("pointerdown", onInteraction);
    window.removeEventListener("touchstart", onInteraction);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  }
  initialized = false;
  pageLoadedAt = Date.now();
  lastInteractionAt = pageLoadedAt;
  lastBecameVisibleAt = pageLoadedAt;
}
